import { z } from "zod";

/**
 * The Categories block — the second data block, and the first whose query is a
 * hand-picked list rather than a filter.
 *
 * A "category" is a `facet_values` row under one of the two taxonomy facet
 * types: `destination` (Destinations) or `activity` (Activity Types). Those are
 * the two slugs traveler-api's destination and experience-type modules read,
 * and the two catalogue pages a card can link to.
 */
export const CATEGORY_CARDS_TYPE = "category-cards";

export const CATEGORY_KINDS = ["destination", "activity"] as const;
export type CategoryKind = (typeof CATEGORY_KINDS)[number];

/** The same payload budget as the Tours block's `MAX_LIMIT`: every card rides
 *  the page's cached API response. */
export const MAX_CATEGORY_CARDS = 24;

const uuid = z.string().uuid();

/**
 * `"a,b,c"` → `["a","b","c"]`, in the order the operator picked them.
 *
 * STORED AS IDS, NOT SLUGS. A slug changes when an operator renames a
 * category, and a block keyed on it would go quietly empty. A comma list
 * rather than JSON because a block's configuration never lives in a JSON
 * attribute (packages/page-builder/CLAUDE.md, Do / Don't).
 *
 * Anything that is not a uuid is dropped here, so the reader never sends a
 * malformed id to Postgres, where a uuid column rejects the whole query.
 *
 * The Tours block reads its picked categories and tours with this too
 * (`tour-cards.ts`), under the same cap. The Blog Posts block reads its picks
 * with `readIdListUpTo` under its own cap (`blog-posts.ts`).
 */
export const readIdListUpTo =
  (max: number) =>
  (value: unknown): string[] => {
    const ids = String(value ?? "")
      .split(",")
      .map((id) => id.trim())
      .filter((id) => uuid.safeParse(id).success);
    return [...new Set(ids)].slice(0, max);
  };

export const readIdList = readIdListUpTo(MAX_CATEGORY_CARDS);

/**
 * The query, parsed from the node's attribute bag.
 *
 * `showTourCount` is NOT part of it. The count is always read — it is one
 * grouped query over rows already being fetched — and whether to show it is a
 * render decision. Keeping it out of the query means toggling the switch never
 * changes which content is fetched, and two blocks that differ only in that
 * switch dedupe to one read.
 */
export const categoryCardsQuerySchema = z.object({
  categories: z.unknown().optional().transform(readIdList),
});

export type CategoryCardsQuery = z.infer<typeof categoryCardsQuerySchema>;

/** One card, projected — not a facet DTO. */
export interface CategoryCard {
  id: string;
  slug: string;
  kind: CategoryKind;
  label: string;
  imageUrl: string | null;
  /** Published, public, non-deleted tours tagged with this category — the same
   *  count the destination and tour-type pages show. */
  tourCount: number;
}

export const categoryCardsQueryKey = (query: CategoryCardsQuery): string =>
  [CATEGORY_CARDS_TYPE, ...query.categories].join("|");

/**
 * The catalogue page a card links to. Built from the slug, never from an
 * operator-typed URL, so no trait on this block needs scheme-checking. These
 * are the storefront's own routes (`sites/[agency]/destinations/[slug]` and
 * the `/tour-types/` path `_experience-card.tsx` links a style chip to).
 */
export const categoryCardPath = (card: Pick<CategoryCard, "kind" | "slug">) =>
  card.kind === "activity"
    ? `/tour-types/${card.slug}/`
    : `/destinations/${card.slug}/`;

/**
 * HTML boolean-attribute semantics, as the carousel's settings read them:
 * presence means true, and the one spelling a round-trip can produce for
 * "off" is `false`.
 */
export const readShowTourCount = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value;
  return String(value).toLowerCase() !== "false";
};

/**
 * How each card is drawn. `caption` puts the name under the picture;
 * `overlay` writes it over the foot of a portrait picture; `tile` centres it
 * on a landscape picture. A render decision, like `showTourCount`, so it is
 * not part of the query either.
 */
export const CATEGORY_CARD_STYLES = ["caption", "overlay", "tile"] as const;

export type CategoryCardStyle = (typeof CATEGORY_CARD_STYLES)[number];

/** A stored value this build does not know falls back to `caption`. */
export const readCardStyle = (value: unknown): CategoryCardStyle =>
  CATEGORY_CARD_STYLES.find((style) => style === value) ?? "caption";
